import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  signInAnonymously,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  createUserProfile,
  ensureUserProfile,
  isAdminRole,
  isSuperAdmin as checkSuperAdmin,
  isUserDisabled,
  updateUserRole,
  USERS_COLLECTION,
} from '../services/userProfile';
import { setAnalyticsActor } from '../analytics/analyticsActor';

const AuthContext = createContext(null);
const LikesContext = createContext(null);
const googleProvider = new GoogleAuthProvider();

function likesSignature(profile) {
  return JSON.stringify({
    e: profile?.likedEvents ?? null,
    l: profile?.likedLocations ?? null,
    d: isUserDisabled(profile),
  });
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [role, setRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  /** True after first users/{uid} snapshot for the signed-in user (exists or not). */
  const [profileReady, setProfileReady] = useState(false);
  const likesCacheRef = useRef({ sig: '', value: { likedEvents: null, likedLocations: null, disabled: false } });
  const bootstrappedSuperAdminRef = useRef(false);

  useEffect(() => {
    let unsubProfile = null;

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (unsubProfile) {
        unsubProfile();
        unsubProfile = null;
      }

      if (user && !user.isAnonymous) {
        setCurrentUser(user);
        setUserProfile(null);
        setRole(null);
        setAnalyticsActor({ uid: user.uid, role: null });
        setProfileReady(false);
        // Must re-block gates on every sign-in / token refresh path — otherwise
        // role checks run with role=null and redirect/blank before the snapshot.
        setAuthLoading(true);
        unsubProfile = onSnapshot(
          doc(db, USERS_COLLECTION, user.uid),
          (snap) => {
            if (snap.exists()) {
              const data = { id: snap.id, ...snap.data() };
              const nextRole = data.role || null;
              setUserProfile(data);
              setRole(nextRole);
              setAnalyticsActor({ uid: user.uid, role: nextRole });
              const email = user.email || data.email;
              if (
                !bootstrappedSuperAdminRef.current
                && checkSuperAdmin({ role: nextRole, email })
                && nextRole !== 'superAdmin'
              ) {
                bootstrappedSuperAdminRef.current = true;
                updateUserRole(user.uid, 'superAdmin').catch((err) => {
                  bootstrappedSuperAdminRef.current = false;
                  console.error('Failed to bootstrap superAdmin role:', err);
                });
              }
            } else {
              setUserProfile(null);
              setRole(null);
              setAnalyticsActor({ uid: user.uid, role: null });
            }
            setProfileReady(true);
            setAuthLoading(false);
          },
          (err) => {
            console.error('Error listening to user profile:', err);
            setUserProfile(null);
            setRole(null);
            setAnalyticsActor({ uid: user.uid, role: null });
            setProfileReady(true);
            setAuthLoading(false);
          }
        );
      } else {
        bootstrappedSuperAdminRef.current = false;
        setCurrentUser(null);
        setUserProfile(null);
        setRole(null);
        setAnalyticsActor({ uid: null, role: null });
        setProfileReady(true);
        setAuthLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (unsubProfile) unsubProfile();
    };
  }, []);

  const signIn = useCallback(async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred;
  }, []);

  const sendPasswordReset = useCallback(async (email) => {
    await sendPasswordResetEmail(auth, String(email || '').trim());
  }, []);

  const signUp = useCallback(async (email, password, displayName = '', opts = {}) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await createUserProfile(cred.user.uid, {
      email,
      displayName,
      role: opts.role === 'partner' ? 'partner' : 'user',
    });
    return cred;
  }, []);

  const signInWithGoogle = useCallback(async (opts = {}) => {
    const cred = await signInWithPopup(auth, googleProvider);
    await ensureUserProfile(cred.user, {
      role: opts.role === 'partner' ? 'partner' : 'user',
    });
    return cred;
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
    try {
      await signInAnonymously(auth);
    } catch (err) {
      console.warn('Anonymous re-sign-in after logout failed:', err);
    }
  }, []);

  const email = currentUser?.email || userProfile?.email || null;
  const isAdmin = isAdminRole(role) || checkSuperAdmin({ role, email });
  const isSuperAdmin = checkSuperAdmin({ role, email });

  const authValue = useMemo(
    () => ({
      currentUser,
      role,
      userProfile,
      authLoading,
      profileReady,
      isAdmin,
      isSuperAdmin,
      signIn,
      signUp,
      signInWithGoogle,
      sendPasswordReset,
      signOut,
    }),
    [currentUser, role, userProfile, authLoading, profileReady, isAdmin, isSuperAdmin, signIn, signUp, signInWithGoogle, sendPasswordReset, signOut]
  );

  const likesValue = useMemo(() => {
    const next = {
      likedEvents: userProfile?.likedEvents ?? null,
      likedLocations: userProfile?.likedLocations ?? null,
      disabled: isUserDisabled(userProfile),
    };
    const sig = likesSignature(userProfile);
    if (likesCacheRef.current.sig === sig) {
      return likesCacheRef.current.value;
    }
    likesCacheRef.current = { sig, value: next };
    return next;
  }, [userProfile]);

  return (
    <AuthContext.Provider value={authValue}>
      <LikesContext.Provider value={likesValue}>{children}</LikesContext.Provider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

/** Narrow likes slice — LikeButton can skip re-renders when unrelated profile fields change. */
export const useLikes = () => {
  const ctx = useContext(LikesContext);
  if (!ctx) throw new Error('useLikes must be used within AuthProvider');
  return ctx;
};
